import { ActionIcon, Group, Loader, Menu, Text, Tooltip } from '@mantine/core';
import { closeAllModals, openConfirmModal } from '@mantine/modals';
import { IconCopy, IconDots } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DatasetsInfo, useBotDatasetMethod, useCollaborateStore } from '@mygpt/app';
import { SvgLinkOff } from '@/components/Embeddings/icon';
import { useDatasetStore } from '@/store/dataset';
import { useGPTStore } from '@/store';

export const DatasetNavItem = ({
  botId,
  currentDatasetId,
  handleUnbind,
  item,
}: {
  item: DatasetsInfo;
  currentDatasetId: string;
  botId: string;
  handleUnbind: (id: string) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAssist } = useCollaborateStore();
  const { setCloneTarget } = useDatasetStore();
  const { botDatasetBindOrUnbind, loading } = useBotDatasetMethod();
  const { userInfo, robotInfo, assetScope } = useGPTStore();

  // 模式判断
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const isCloudEnterprise = !isLocalMode && !!assetScope;

  // 权限判断：本地部署和Cloud企业模式下判断是否为OWNER
  const isOwner = useMemo(() => {
    if (isCloudEnterprise) {
      if (robotInfo?.permission_type) {
        return robotInfo.permission_type === 'OWNER';
      }
      return userInfo?.user_id === robotInfo?.user?.user_id;
    }
    if (isLocalMode) {
      return userInfo?.email === robotInfo?.user?.email;
    }
    return true;
  }, [
    isLocalMode,
    isCloudEnterprise,
    userInfo?.user_id,
    userInfo?.email,
    robotInfo?.user?.user_id,
    robotInfo?.user?.email,
    robotInfo?.permission_type,
  ]);

  // 判断是否应该隐藏操作按钮（isAssist模式或非owner用户）
  const shouldHideActions = useMemo(() => {
    return isAssist || !isOwner;
  }, [isAssist, isOwner]);

  const goToList = (datasetId: string) => {
    isAssist
      ? navigate(`/assist/${botId}/datasets/${datasetId}/data-source/file`)
      : navigate(`/bot/${botId}/datasets/${datasetId}/data-source/file`);
  };

  const isCloning = useMemo(() => {
    return ['cloning_source', 'cloning_target'].includes(item.data_status || '');
  }, [item.data_status]);

  const unbindDataset = (dataset: DatasetsInfo) => {
    openConfirmModal({
      title: t('embeddings.dataset.remove_modal.title'),
      centered: true,
      children: <span>{t('embeddings.dataset.remove_modal.desc')}</span>,
      labels: { confirm: t('alert_ok'), cancel: t('alert_cancel') },
      onCancel: () => console.log('Cancel'),
      confirmProps: {
        loading: loading,
        onClick: async () => {
          await botDatasetBindOrUnbind({
            aiId: botId || '',
            datasetIds: [dataset.id],
            type: 'unbind',
          });
          closeAllModals();
          handleUnbind(dataset.id);
          window.location.href = `/bot/${botId}/data-source`;
        },
      },
    });
  };

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      className={`group max-w-[215px] p-2 rounded-lg ${
        item.id === currentDatasetId
          ? 'bg-violet-100 text-violet-500 cursor-default'
          : 'cursor-pointer hover:bg-violet-50 hover:text-violet-500'
      }`}
      onClick={(e) => {
        goToList(item.id);
      }}
    >
      <Group gap={6} wrap="nowrap" className="flex-1 min-w-0">
        <Text size={'13px'} lineClamp={1}>
          {item.name}
        </Text>
        {isCloning && (
          <Tooltip
            label={item.data_status === 'cloning_source' ? t('dataset.copying') : t('dataset.generating')}
            withArrow
            position="right"
          >
            <Loader size={12} color="violet" />
          </Tooltip>
        )}
      </Group>
      {!shouldHideActions && (
        <Menu shadow="md" trigger="click-hover" position="right-start" withArrow>
          <Menu.Target>
            <ActionIcon
              size="sm"
              className="invisible group-hover:visible"
              variant="subtle"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <IconDots size="1.125rem" />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconCopy size={14} stroke={1} />}
              onClick={(e) => {
                e.stopPropagation();
                setCloneTarget(item);
              }}
              disabled={isCloning}
            >
              {isCloning
                ? item.data_status === 'cloning_source'
                  ? t('dataset.copying')
                  : t('dataset.generating')
                : t('clone')}
            </Menu.Item>
            <Menu.Item
              leftSection={
                <span className="scale-50 w-[14px]">
                  <SvgLinkOff />
                </span>
              }
              onClick={(e) => {
                e.stopPropagation();
                unbindDataset(item);
              }}
            >
              {t('embeddings.dataset.remove')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
};
